<template>
  <div class="row vh-100 signin-container">
    <div class="col vh-100 signin-container__left-side">
      <div class="d-flex justify-content-end">
        <div class="d-flex align-items-center">
          <span class="data-label mt-2">{{ $t('HeaderLanguageTitle') }}:</span>
          <custom-drop-action-item
            class="mt-2"
            id="ddlLanguage"
            text="Idioma"
            set-selected-option
            v-model="selectedLanguage"
            :items="languageOptions"
            @click="onChangeLanguageHandler"
          />
        </div>
      </div>
      <div class="text-center mt-3 mt-md-0">
        <img src="@/assets/img/signin-logo.png" alt="signin-logo" />
      </div>
      <div class="d-flex flex-column align-items-center mt-5 px-4 px-md-0">
        <validation-observer
          ref="signInFormRef"
          v-slot="{}"
          tag="form"
          v-on:keyup.enter="onSigInHandler"
        >
          <div class="text-center">
            <div class="text-left">
              <div class="color-primary-4 font-14 font-weight-bold">
                {{ $t('LoginUserName') }}:
              </div>
              <validation-provider rules="required" v-slot="{ errors }">
                <custom-input
                  v-model="model.username"
                  :error="errors[0]"
                  custom-error-msg="Campo Requerido."
                />
              </validation-provider>
            </div>
            <div class="mt-4 text-left">
              <div class="color-primary-4 font-14 font-weight-bold">
                {{ $t('Password') }}:
              </div>
              <validation-provider rules="required" v-slot="{ errors }">
                <custom-input
                  type="password"
                  v-model="model.password"
                  :error="errors[0]"
                  custom-error-msg="Campo Requerido."
                />
              </validation-provider>
            </div>
            <div
              class="d-flex flex-column flex-md-row mt-2 text-left align-items-center align-items-md-start"
            >
              <span
                class="flex-grow-1 text-underline color-primary-6 font-14"
                @click="onOpenForgotPasswordHandler"
              >
                {{ $t('ForgotYourPassword') }}
              </span>
              <span
                class="text-underline color-primary-6 font-14"
                @click="onOpenForgotAnswerHandler"
              >
                {{ $t('ForgotYourSecurityAnswer') }}
              </span>
            </div>
            <div class="mt-4">
              <action-button
                class="btn-success px-4"
                :text="$t('LogIn')"
                iright="fa fa-arrow-right"
                @click="onSigInHandler"
              />
            </div>
            <div class="mt-4">
              <span class="color-black-1 font-14 d-block">
                {{ $t('YouDoNotHaveAccount') }}
              </span>
              <span
                class="text-underline color-green-2 font-14 font-weight-bold"
                @click="onRegisterHandler"
              >
                {{ $t('RegisterMe') }}
              </span>
            </div>
            <div class="mt-4 font-12 color-black-1">
              {{ $t('ForSupportOfThisApplicationContactThe') }}
              <br />
              <span class="color-green-2 font-weight-bold">
                Prof. Israel Sánchez, Lic. Cristina G. Cruz Rivera
              </span>
              {{ $t('ViaEmailTo') }}:
              <span
                class="text-underline d-block color-primary-6 font-12 font-weight-bold mt-1"
              >
                sanchez_is@de.pr.gov
                <br />
                cruzrc@de.pr.gov
              </span>
            </div>
            <div class="font-12 mt-4" style="max-width: 400px">
              <em class="fa fa-circle-info color-green-2 font-11" />
              <span class="ml-1 color-green-2 font-weight-bold">
                {{ $t('lblImportant') }}:
              </span>
              <span>{{ $t('YouMustReadTheUserManual') }}.</span>
            </div>
            <div class="mt-4 d-flex flex-column align-items-center pl-5 ml-5">
              <div
                v-for="(item, index) in accessOptions"
                :key="`access-opt-${index}`"
                style="width: 200px"
                class="mb-2"
              >
                <div
                  class="access-option d-flex align-items-center cursor-pointer"
                >
                  <a
                    class="color-primary-4 font-16"
                    target="_blank"
                    :href="item.href"
                  >
                    <em class="mr-1" :class="`fas fa-${item.icon}`" />
                    {{ item.label }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </validation-observer>
      </div>
    </div>
    <div class="col vh-100 signin-container__right-side d-none d-md-block">
      <signin-header-panel
        class="signin-header-container"
        @on-register="onRegisterHandler"
      />
      <img src="@/assets/img/signin-background.png" alt="signin-background" />
    </div>
    <div class="col-12">
      <forgot-password
        ref="forgotPasswordModalRef"
        @cancel="onCancelForgotPasswordHandler"
        @finish="onFinishForgotPasswordHandler"
      />
      <forgot-security-answer
        ref="forgotSecurityAnswerModalRef"
        @cancel="onCancelForgotAnswerHandler"
        @finish="onFinishForgotAnswerHandler"
      />
    </div>
  </div>
</template>

<script>
//third party libs
import { mapActions } from 'vuex';

//libs
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';

//views and components
import ForgotPassword from './ForgotPassword.vue';
import ForgotSecurityAnswer from './ForgotSecurityAnswer.vue';
import { SigninHeaderPanel } from './components';
import { CustomDropActionItem } from '@/components/common';
//store
import { name as userModuleName } from '@/store/modules/user';

//mixins
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

//constants
const HOME_ROUTE = '/';
const REGISTER_ROUTE = '/register';
import languageOptionsEnum from '@/utils/constants/languageOptions.js';

//i18n
import { i18n, loadLanguageAsync } from '@/i18n';
import lang from '@/utils/constants/lang';

export default {
  name: 'SignIn',
  mixins: [alertMixin],
  components: {
    CustomInput,
    ActionButton,
    SigninHeaderPanel,
    ForgotPassword,
    ForgotSecurityAnswer,
    CustomDropActionItem,
  },
  data: () => ({
    model: {
      username: null,
      password: null,
    },
    selectedLanguage: null,
  }),
  computed: {
    accessOptions() {
      return [
        {
          label: i18n.t('VirtualLibrary'),
          icon: 'atlas',
          href: 'https://ciie.dde.pr/BibliotecaVirtual/',
        },
        {
          label: i18n.t('LnkCircularLetter'),
          icon: 'file-alt',
          href: '/carta-circular.pdf',
        },
        {
          label: i18n.t('UserManual'),
          icon: 'file-alt',
          href: '/manual-solicitante.pdf',
        },
      ];
    },
    languageOptions() {
      return languageOptionsEnum;
    },
  },
  async mounted() {
    await loadLanguageAsync();
    this.loadSelectedLanguage();
  },
  methods: {
    ...mapActions(userModuleName, ['login']),
    async onSigInHandler() {
      const isValid = await this.$refs.signInFormRef.validate();
      if (!isValid) {
        this.ShowToast(
          i18n.t('Alert'),
          `${i18n.t('PleaseCompleteRequestedInformation')}.`,
          'error'
        );
        return;
      }
      const { username, password } = this.model;
      if (username && password) {
        const userResult = await this.login({ username, password });
        if (userResult) {
          if (!userResult.validator.isValid) {
            this.ShowToast(
              'Error',
              this.userSelectedLang === lang.ES
                ? userResult.validator.message
                : userResult.validator.messageEn,
              'error'
            );
            return;
          }
          //navigate to dashboard
          this.$router.replace({
            path: HOME_ROUTE,
          });
        }
      }
    },

    //recovery password
    onOpenForgotPasswordHandler() {
      this.$refs.forgotPasswordModalRef?.showModal();
    },
    onCancelForgotPasswordHandler() {
      this.$refs.forgotPasswordModalRef?.hideModal();
    },
    onFinishForgotPasswordHandler() {
      this.$refs.forgotPasswordModalRef?.hideModal();
    },

    //recovery security answer
    onOpenForgotAnswerHandler() {
      this.$refs.forgotSecurityAnswerModalRef?.showModal();
    },
    onCancelForgotAnswerHandler() {
      this.$refs.forgotSecurityAnswerModalRef?.hideModal();
    },
    onFinishForgotAnswerHandler() {
      this.$refs.forgotSecurityAnswerModalRef?.hideModal();
    },
    onRegisterHandler() {
      this.$router.push({
        path: REGISTER_ROUTE,
      });
    },
    loadSelectedLanguage() {
      this.selectedLanguage = this.languageOptions.find(
        (x) => x.id === this.userSelectedLang
      );
    },
    onChangeLanguageHandler(value) {
      if (value?.id) {
        this.$i18n.locale = value.id;
        window.location.reload();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/components/action-button/index.scss';

.signin-container {
  &__left-side {
    img {
      width: 345px;
    }
  }
  &__right-side {
    img {
      width: 100%;
      height: 100%;
    }
  }
  .text-underline {
    cursor: pointer;
    font-weight: 600px;
    text-decoration: underline;
  }
}

.signin-header-container {
  position: absolute;
  width: 91% !important;
  margin: 20px;
}

.access-option {
  user-select: none;
}

.access-option :hover {
  color: #105d62 !important;
}
</style>
